<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { initializeSectionTitles, getHintForProgress } from '@/Helpers/MerchantGenerationHintGenerator';

const sections = ref([]);
const currentHint = ref('');
const hintUpdateInterval = ref(null);

const progress = ref(0);
const progressInterval = ref(null);
const generationStartTime = ref(null);

function updateHint() {
    currentHint.value = getHintForProgress(progress.value, sections.value);
}

onMounted(() => {
    sections.value = initializeSectionTitles();
    updateHint();
    hintUpdateInterval.value = setInterval(updateHint, 8000);
});

onUnmounted(() => {
    if (hintUpdateInterval.value) {
        clearInterval(hintUpdateInterval.value);
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
    }
});

function calculateProgress(elapsedSeconds) {
    if (elapsedSeconds <= 45) {
        const t = elapsedSeconds / 45;
        return (1 - (1 - t) * (1 - t)) * 95;
    } else {
        return 95 + 5 * (1 - 1 / (1 + (elapsedSeconds - 45) / 60));
    }
}

function startProgressInterval() {
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
    }

    progressInterval.value = setInterval(() => {
        const elapsed = (Date.now() - generationStartTime.value) / 1000;
        progress.value = calculateProgress(elapsed);
    }, 100);
}

function startProgress() {
    progress.value = 0;
    generationStartTime.value = Date.now();
    startProgressInterval();
}

function resumeProgress(elapsedSeconds) {
    generationStartTime.value = Date.now() - (elapsedSeconds * 1000);
    progress.value = calculateProgress(elapsedSeconds);
    startProgressInterval();
}

function completeProgress() {
    const targetProgress = 100;
    const currentProgress = progress.value;
    const increment = (targetProgress - currentProgress) / 10;

    let steps = 0;
    const completeInterval = setInterval(() => {
        steps++;
        progress.value = Math.min(currentProgress + (increment * steps), targetProgress);

        if (progress.value >= targetProgress) {
            clearInterval(completeInterval);
            if (progressInterval.value) {
                clearInterval(progressInterval.value);
                progressInterval.value = null;
            }
        }
    }, 100);
}

function resetProgress() {
    progress.value = 0;
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
}

defineExpose({
    startProgress,
    resumeProgress,
    completeProgress,
    resetProgress,
    progress
});
</script>

<template>
    <div class="merchant-generation-progress w-full">
        <p class="text-sm text-amber-300 italic animate-fade-in mb-2 truncate">
            {{ currentHint }}
        </p>

        <div class="relative w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
                class="h-full bg-amber-500 rounded-full transition-all duration-300 ease-out"
                :style="{ width: Math.round(progress || 0) + '%' }"
            >
                <div v-if="progress > 0 && progress < 100" class="absolute inset-0 shimmer"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes shimmer {
    0% { background-position: -100% 0; }
    100% { background-position: 200% 0; }
}

.shimmer {
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
}

@keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

.animate-fade-in {
    animation: fade-in 0.5s ease-out;
}
</style>
